import { createFileRoute } from '@tanstack/react-router'
import { useTranslations, useLocale } from 'use-intl'

export const Route = createFileRoute('/{-$locale}/_public/privacy-policy')({
  component: PrivacyPolicyPage,
})

const content = {
  en: {
    lastUpdated: 'Last Updated: May 15, 2026',
    sections: [
      {
        title: '1. Introduction',
        body: 'Welcome to TabeTabi. We respect your privacy and are committed to protecting your personal data. This Privacy Policy will inform you as to how we look after your personal data and Google user data when you visit our website.'
      },
      {
        title: '2. What Google User Data We Collect',
        body: 'When you sign in using Google OAuth, the data we collect about you is what you have provided to us, including your name, email address, and profile picture. We also collect Profile Data (your favorites, private dining notes, preferences) and Usage Data (your interactions with our map).'
      },
      {
        title: '3. How We Use Your Google User Data',
        body: 'We will use your Google user data and personal data only to provide you with the services you requested, such as creating your user account, synchronizing your saved restaurants across devices, and providing personalized recommendations. We strictly prohibit the use of Google user data for targeted advertising, selling to data brokers, or training AI models.'
      },
      {
        title: '4. Data Sharing and Disclosure',
        body: 'We do not transfer or disclose your Google user data or personal information to third parties for purposes other than the ones provided. We do not sell your data to third-parties. Your private dining notes are not shared with the public, other users, or restaurants.'
      },
      {
        title: '5. Data Security',
        body: 'We use encryption to protect your information. Security procedures are in place to protect the confidentiality of your data and prevent your personal data from being accidentally lost, used or accessed in an unauthorized way.'
      },
      {
        title: '6. Data Retention and Deletion',
        body: 'We will retain your personal information for the length of time needed to fulfill the purposes outlined in this privacy policy unless a longer retention period is required or permitted by law. When the data retention period expires, we will delete or destroy it. You may request for your data to be deleted at any time by contacting us at support@tabetabi.cc.'
      },
      {
        title: '7. Cookies and Third-Party Links',
        body: 'We use cookies to manage user sessions and keep you logged in. Our application integrates with Google Maps for location services. Clicking on external links (like Tabelog) may allow third parties to collect data. We are not responsible for their privacy statements.'
      },
      {
        title: '8. Contact Us',
        body: 'If you have any questions about this privacy policy, please contact us at support@tabetabi.cc.'
      }
    ]
  },
  'zh-CN': {
    lastUpdated: '最后更新日期：2026年5月15日',
    sections: [
      {
        title: '1. 引言',
        body: '欢迎使用 TabeTabi (食べ旅)。我们非常重视您的隐私，并致力于保护您的个人数据。本隐私政策将告知您我们如何处理和保护您的个人数据以及 Google 用户数据。'
      },
      {
        title: '2. 我们收集哪些 Google 用户数据',
        body: '当您使用 Google OAuth 登录时，我们收集的关于您的数据是您提供给我们的数据，包括您的姓名、电子邮件地址和个人资料图片。我们还会收集档案数据（您收藏的餐厅、私人笔记、偏好设置）以及使用数据（您与地图交互的习惯）。'
      },
      {
        title: '3. 我们如何使用您的 Google 用户数据',
        body: '我们将仅使用您的 Google 用户数据和个人数据来向您提供您请求的服务，例如创建您的用户账户、跨设备同步您的餐厅收藏夹，以及提供个性化推荐。我们严格禁止将 Google 用户数据用于定向广告、出售给数据经纪人或用于训练 AI 模型。'
      },
      {
        title: '4. 数据共享与披露',
        body: '除提供既定服务外，我们绝不会出于其他目的向第三方转移或披露您的 Google 用户数据或个人信息。我们绝不出售您的数据。您为餐厅添加的“私人笔记”绝不会向公众、其他用户或餐厅商家共享。'
      },
      {
        title: '5. 数据安全',
        body: '我们使用加密技术来保护您的信息。我们已采取严格的安全程序来保护您数据的机密性，以防止您的个人数据被意外丢失、未经授权地使用或访问。'
      },
      {
        title: '6. 数据保留与删除',
        body: '我们将根据实现本隐私政策所述目的所需的时间保留您的个人信息，除非法律要求或允许更长的保留期限。当特定类型数据的保留期届满时，我们将会将其删除或销毁。您可以随时通过 support@tabetabi.cc 联系我们，要求删除您的数据。'
      },
      {
        title: '7. Cookie 与第三方链接',
        body: '我们使用 Cookie 来维持您的登录状态。我们的应用程序集成了 Google Maps 以提供地图服务。点击外部链接（如 Tabelog）可能导致第三方收集您的数据。我们不对其隐私声明负责。'
      },
      {
        title: '8. 联系我们',
        body: '如果您对本隐私政策有任何疑问，请通过 support@tabetabi.cc 联系我们。'
      }
    ]
  },
  'zh-TW': {
    lastUpdated: '最後更新日期：2026年5月15日',
    sections: [
      {
        title: '1. 引言',
        body: '歡迎使用 TabeTabi (食べ旅)。我們非常重視您的隱私，並致力於保護您的個人資料。本隱私政策將告知您我們如何處理和保護您的個人資料以及 Google 使用者資料。'
      },
      {
        title: '2. 我們收集哪些 Google 使用者資料',
        body: '當您使用 Google OAuth 登入時，我們收集的關於您的資料是您提供給我們的資料，包括您的姓名、電子郵件地址和個人資料圖片。我們還會收集檔案資料（您收藏的餐廳、私人筆記、偏好設定）以及使用資料（您與地圖互動的習慣）。'
      },
      {
        title: '3. 我們如何使用您的 Google 使用者資料',
        body: '我們將僅使用您的 Google 使用者資料和個人資料來向您提供您請求的服務，例如建立您的使用者帳戶、跨裝置同步您的餐廳收藏夾，以及提供個人化推薦。我們嚴格禁止將 Google 使用者資料用於定向廣告、出售給資料經紀人或用於訓練 AI 模型。'
      },
      {
        title: '4. 資料共享與披露',
        body: '除提供既定服務外，我們絕不會出於其他目的向第三方轉移或披露您的 Google 使用者資料或個人資訊。我們絕不出售您的資料。您為餐廳添加的「私人筆記」絕不會向公眾、其他使用者或餐廳商家共享。'
      },
      {
        title: '5. 資料安全',
        body: '我們使用加密技術來保護您的資訊。我們已採取嚴格的安全程序來保護您資料的機密性，以防止您的個人資料被意外遺失、未經授權地使用或存取。'
      },
      {
        title: '6. 資料保留與刪除',
        body: '我們將根據實現本隱私政策所述目的所需的時間保留您的個人資訊，除非法律要求或允許更長的保留期限。當特定類型資料的保留期屆滿時，我們將會將其刪除或銷毀。您可以隨時透過 support@tabetabi.cc 聯絡我們，要求刪除您的資料。'
      },
      {
        title: '7. Cookie 與第三方連結',
        body: '我們使用 Cookie 來維持您的登入狀態。我們的應用程式整合了 Google Maps 以提供地圖服務。點擊外部連結（如 Tabelog）可能導致第三方收集您的資料。我們不對其隱私聲明負責。'
      },
      {
        title: '8. 聯絡我們',
        body: '如果您對本隱私政策有任何疑問，請透過 support@tabetabi.cc 聯絡我們。'
      }
    ]
  },
  ja: {
    lastUpdated: '最終更新日：2026年5月15日',
    sections: [
      {
        title: '1. はじめに',
        body: 'TabeTabi（食べ旅）へようこそ。当社はお客様のプライバシーを尊重し、個人データの保護に努めています。本プライバシーポリシーでは、お客様の個人データおよびGoogleユーザーデータの取り扱いと保護についてご説明します。'
      },
      {
        title: '2. 収集するGoogleユーザーデータ',
        body: 'Google OAuthを使用してログインする際、当社が収集するお客様のデータは、氏名、メールアドレス、プロフィール画像など、お客様が当社に提供したものです。また、プロフィールデータ（お気に入り、プライベートメモ、設定）および利用データ（マップの操作履歴など）も収集します。'
      },
      {
        title: '3. Googleユーザーデータの利用目的',
        body: '当社は、ユーザーアカウントの作成、デバイス間でのブックマーク同期、パーソナライズされたおすすめの提供など、お客様が希望するサービスを提供するためにのみ、Googleユーザーデータおよび個人データを使用します。Googleユーザーデータをターゲティング広告、データブローカーへの販売、AIモデルのトレーニングに使用することは厳格に禁止されています。'
      },
      {
        title: '4. データの共有と開示',
        body: '当社は、提供された目的以外の目的で、Googleユーザーデータや個人情報を第三者に譲渡または開示することはありません。お客様のデータを販売することはありません。お客様の「プライベートメモ」が一般公開されたり、他のユーザーや店舗と共有されることはありません。'
      },
      {
        title: '5. データの安全性',
        body: '当社は、お客様の情報を保護するために暗号化を使用しています。データの機密性を保護し、個人データの紛失や不正利用を防ぐためのセキュリティ対策が講じられています。'
      },
      {
        title: '6. データの保持と削除',
        body: '当社は、法律でより長い保持期間が義務付けられている、または許可されている場合を除き、本プライバシーポリシーに記載された目的を達成するために必要な期間、個人情報を保持します。保存期間が終了したデータは削除または破棄します。お客様は、support@tabetabi.cc に連絡することで、いつでもデータの削除をリクエストできます。'
      },
      {
        title: '7. クッキーと外部リンク',
        body: '当社はログイン状態を維持するためにクッキーを使用しています。当サービスはGoogle Mapsと連携しています。外部リンク（食べログなど）をクリックすると、第三者がデータを収集する場合があります。当社は外部サイトのプライバシーポリシーについて責任を負いません。'
      },
      {
        title: '8. お問い合わせ',
        body: '本プライバシーポリシーに関するご質問は、support@tabetabi.cc までご連絡ください。'
      }
    ]
  },
  ko: {
    lastUpdated: '최종 수정일: 2026년 5월 15일',
    sections: [
      {
        title: '1. 소개',
        body: 'TabeTabi(타베타비)에 오신 것을 환영합니다. 당사는 귀하의 개인정보를 존중하며 개인 데이터를 보호하기 위해 최선을 다하고 있습니다. 본 개인정보 처리방침은 귀하의 개인 데이터 및 Google 사용자 데이터를 처리하고 보호하는 방법을 설명합니다.'
      },
      {
        title: '2. 수집하는 Google 사용자 데이터',
        body: 'Google OAuth를 사용하여 로그인할 때 당사가 수집하는 데이터는 이름, 이메일 주소, 프로필 사진 등 귀하가 당사에 제공한 데이터입니다. 또한 프로필 데이터(즐겨찾기, 개인 메모, 설정) 및 사용 데이터(지도 상호작용)도 수집합니다.'
      },
      {
        title: '3. Google 사용자 데이터 사용 방법',
        body: '당사는 사용자 계정 생성, 기기 간 즐겨찾기 동기화, 맞춤형 추천 제공 등 귀하가 요청한 서비스를 제공하기 위해서만 Google 사용자 데이터 및 개인 데이터를 사용합니다. Google 사용자 데이터를 타겟팅 광고, 데이터 브로커에게 판매 또는 AI 모델 학습에 사용하는 것은 엄격히 금지됩니다.'
      },
      {
        title: '4. 데이터 공유 및 공개',
        body: '당사는 제공된 목적 외의 목적으로 Google 사용자 데이터나 개인 정보를 타사에 전송하거나 공개하지 않습니다. 당사는 귀하의 데이터를 판매하지 않습니다. 귀하의 개인 메모는 대중, 다른 사용자 또는 레스토랑과 공유되지 않습니다.'
      },
      {
        title: '5. 데이터 보안',
        body: '당사는 귀하의 정보를 보호하기 위해 암호화를 사용합니다. 데이터의 기밀성을 보호하고 개인 데이터의 우발적인 손실이나 무단 사용을 방지하기 위한 보안 절차가 마련되어 있습니다.'
      },
      {
        title: '6. 데이터 보존 및 삭제',
        body: '당사는 법률에서 더 긴 보존 기간을 요구하거나 허용하지 않는 한 본 개인정보 처리방침에 명시된 목적을 달성하는 데 필요한 기간 동안 개인 정보를 보존합니다. 데이터 보존 기간이 만료되면 해당 데이터를 삭제하거나 파기합니다. 귀하는 언제든지 support@tabetabi.cc 로 연락하여 데이터 삭제를 요청할 수 있습니다.'
      },
      {
        title: '7. 쿠키 및 타사 링크',
        body: '당사는 로그인 상태를 유지하기 위해 쿠키를 사용합니다. 당사 서비스는 위치 서비스를 위해 Google Maps와 통합되어 있습니다. 외부 링크(예: 타베로그)를 클릭하면 타사가 데이터를 수집할 수 있습니다. 당사는 타사 웹사이트의 개인정보 처리방침에 대해 책임지지 않습니다.'
      },
      {
        title: '8. 문의하기',
        body: '본 개인정보 처리방침에 관한 문의 사항이 있으시면 support@tabetabi.cc 로 연락해 주시기 바랍니다.'
      }
    ]
  }
}

function PrivacyPolicyPage() {
  const t = useTranslations('footer.nav.legal')
  const locale = useLocale() as keyof typeof content
  
  // Fallback to English if locale is not mapped
  const pageContent = content[locale] || content['en']

  return (
    <div className="container mx-auto max-w-4xl px-4 py-12 md:py-20">
      <h1 className="text-3xl md:text-4xl font-bold mb-8 text-foreground">
        {t('privacy')}
      </h1>
      
      <div className="prose prose-stone dark:prose-invert max-w-none">
        <p className="text-lg text-muted-foreground mb-8">
          {pageContent.lastUpdated}
        </p>

        {pageContent.sections.map((section, index) => (
          <section key={index} className="mb-8">
            <h2 className="text-2xl font-semibold mb-4 text-foreground">{section.title}</h2>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {section.body}
            </p>
          </section>
        ))}
      </div>
    </div>
  )
}
